import { Link } from 'react-router-dom';
import { diagnosticsApi } from '../api/endpoints';
import type { Constat, NatureConstat, PieceDeclaree } from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime } from '../lib/format';
import { Badge } from './Badge';
import { Notice } from './Notice';

/**
 * Écart entre ce que l'inventaire déclarait à la date du diagnostic et ce que
 * le rapport décrit.
 *
 * CE SONT DES SIGNALEMENTS À INTERPRÉTER, jamais des erreurs affirmées, et
 * l'écran est écrit pour que cela ne fasse aucun doute. Le rapprochement se
 * fait sur des désignations textuelles de fabricants, sans identifiant commun
 * entre les deux côtés : une barrette remplacée par un modèle équivalent d'une
 * autre marque paraîtra divergente tout en étant parfaitement légitime.
 *
 * Rien ne se corrige d'ici. L'inventaire se modifie par un événement du
 * journal, depuis la fiche du poste ou l'écran d'inventaire — et c'est
 * volontaire : une correction faite depuis un écart constaté serait une
 * correction faite sans regarder le matériel.
 */

const LIBELLES: Record<NatureConstat, string> = {
  concordance: 'Concorde',
  divergence: 'Diffère',
  absentDeLInventaire: 'Absent de l’inventaire',
  absentDuRapport: 'Absent du rapport',
};

const TONS: Record<NatureConstat, 'ok' | 'alerte' | 'accent' | 'neutre'> = {
  concordance: 'ok',
  divergence: 'alerte',
  absentDeLInventaire: 'accent',
  absentDuRapport: 'accent',
};

const EXPLICATIONS: Record<NatureConstat, string> = {
  concordance:
    'Les deux libellés se ressemblent assez pour désigner probablement le même matériel.',
  divergence:
    'Les deux côtés décrivent un matériel, mais les libellés ne se ressemblent pas. ' +
    'Ce peut être un remplacement non enregistré, comme une simple différence de rédaction.',
  absentDeLInventaire:
    'Le rapport décrit ce matériel, l’inventaire n’en déclare pas à cette date. ' +
    'Ce peut être un montage non enregistré, comme une pièce jamais inventoriée.',
  absentDuRapport:
    'L’inventaire déclare ce matériel, le rapport n’en parle pas. ' +
    'Ce peut être un retrait non enregistré, comme une pièce que le rapport ne sait pas lire.',
};

function DeclareCell({ piece }: { piece: PieceDeclaree | null }) {
  if (piece === null) return <span className="faible">—</span>;
  return (
    <>
      {piece.libelle === '' ? piece.designation : piece.libelle}
      <div className="faible">
        {piece.quantity > 1 && `× ${piece.quantity} · `}
        {piece.serialNumber === null ? (
          'sans numéro de série'
        ) : (
          <span className="mono">{piece.serialNumber}</span>
        )}
      </div>
    </>
  );
}

/**
 * Mention portée par les lignes où le cumul est l'explication la plus probable.
 *
 * Elle vient avant l'explication générale, et non après : sur une mémoire ou
 * un stockage, c'est presque toujours la bonne lecture, et la reléguer en
 * second ferait lire d'abord la mauvaise.
 */
function MentionCumul() {
  return (
    <div style={{ marginBottom: 'var(--espace-2)' }}>
      <Badge tone="accent">Cumul probable</Badge>{' '}
      <strong>
        Plusieurs pièces déclarées peuvent apparaître comme une seule valeur dans le
        rapport.
      </strong>{' '}
      Deux barrettes de 8 Go se lisent « 16 Go » : les capacités ne sont pas
      additionnées ici, et cette ligne est alors sans objet.
    </div>
  );
}

function ConstatRow({ constat }: { constat: Constat }) {
  return (
    <tr>
      <td>
        <Badge tone={TONS[constat.nature]}>{LIBELLES[constat.nature]}</Badge>
      </td>
      <td>
        <DeclareCell piece={constat.declare} />
      </td>
      <td>
        {constat.observe === null ? (
          <span className="faible">—</span>
        ) : (
          constat.observe
        )}
      </td>
      <td className="faible">
        {constat.cumulPossible && <MentionCumul />}
        {EXPLICATIONS[constat.nature]}
      </td>
    </tr>
  );
}

export function ConfigurationComparison({
  diagnosticId,
  workstationId,
}: {
  diagnosticId: string;
  workstationId: string;
}) {
  const comparison = useAsync(
    (signal) => diagnosticsApi.comparison(diagnosticId, signal),
    [diagnosticId],
  );

  if (comparison.error !== null) {
    return (
      <Notice tone="erreur" title="Comparaison indisponible">
        {comparison.error}
      </Notice>
    );
  }

  const data = comparison.data?.comparaison ?? null;
  if (data === null) {
    return <div className="chargement">Comparaison en cours…</div>;
  }

  if (!data.comparable) {
    return (
      <Notice tone="info" title="Rien à comparer">
        {data.raison}
        <div style={{ marginTop: 'var(--espace-3)' }}>
          Aucun écart n’est affiché : une absence d’information n’est pas une
          configuration vide, et signaler comme manquant tout ce que l’inventaire
          déclare serait faux.
        </div>
      </Notice>
    );
  }

  const { resume } = data;
  const signalements =
    resume.divergences + resume.absentsDeLInventaire + resume.absentsDuRapport;

  return (
    <div className="pile pile--serree">
      <Notice tone="info" title="À lire comme des signalements, pas comme des erreurs">
        Le rapprochement se fait sur des désignations textuelles de fabricants, sans
        identifiant commun entre l’inventaire et le rapport. Il se trompe, et il se
        trompe surtout d’une façon :
        <p style={{ margin: 'var(--espace-3) 0' }}>
          <strong>
            Plusieurs pièces déclarées apparaissent souvent comme une seule valeur
            observée.
          </strong>{' '}
          Deux barrettes de 8 Go déclarées face à « 16 Go » lus dans le rapport
          ressortent en différence, alors que les deux disent la même chose. C’est le
          cas le plus fréquent, et ce n’est pas une anomalie de votre parc.
        </p>
        Les capacités ne sont volontairement pas additionnées : il faudrait interpréter
        les unités de désignations saisies à la main, et le rapprochement se tromperait
        autrement. Les lignes concernées portent une mention.
        <p style={{ margin: 'var(--espace-3) 0 0' }}>
          Les autres causes d’erreur : un modèle équivalent d’une autre marque paraît
          différent, et deux machines identiques produisent des rapports indiscernables.
          Rien ne se corrige depuis cet écran — l’inventaire se modifie par un
          événement, depuis{' '}
          <Link to={`/postes/${workstationId}`}>la fiche du poste</Link>.
        </p>
      </Notice>

      <div className="ligne">
        <span className="faible">
          Inventaire lu à la date du diagnostic, le {formatDateTime(data.at)} :
        </span>
        <Badge tone="ok">{resume.concordances} concordance(s)</Badge>
        <Badge tone={resume.divergences > 0 ? 'alerte' : 'neutre'}>
          {resume.divergences} différence(s)
        </Badge>
        <Badge tone={resume.absentsDeLInventaire > 0 ? 'accent' : 'neutre'}>
          {resume.absentsDeLInventaire} absent(s) de l’inventaire
        </Badge>
        <Badge tone={resume.absentsDuRapport > 0 ? 'accent' : 'neutre'}>
          {resume.absentsDuRapport} absent(s) du rapport
        </Badge>
      </div>

      {signalements === 0 && data.domaines.length > 0 && (
        <Notice tone="info">
          Aucun écart relevé : tout ce que le rapport décrit se retrouve dans
          l’inventaire à cette date, et réciproquement.
        </Notice>
      )}

      {data.domaines.length === 0 && (
        <p className="doux">
          Le rapport ne décrit aucun des matériels que l’inventaire suit, et
          l’inventaire n’en déclare aucun à cette date.
        </p>
      )}

      {data.domaines.map((domaine) => (
        <section key={domaine.domaine} className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">{domaine.libelle}</span>
          </div>
          <div className="panneau__corps panneau__corps--nu">
            <div className="tableau-cadre">
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Constat</th>
                    <th>Déclaré à l’inventaire</th>
                    <th>Décrit par le rapport</th>
                    <th>Ce que cela peut vouloir dire</th>
                  </tr>
                </thead>
                <tbody>
                  {domaine.constats.map((constat, rang) => (
                    <ConstatRow key={rang} constat={constat} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ))}

      {data.horsPortee.length > 0 && (
        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Hors de portée du rapport</span>
            <span className="faible pousse">
              Déclaré à l’inventaire, jamais décrit par un rapport UserDiag
            </span>
          </div>
          <div className="panneau__corps">
            <p className="faible">
              Ces pièces ne sont pas des écarts. Leur absence du rapport ne dit rien :
              un rapport ne décrit ni l’alimentation, ni la carte mère, ni les
              périphériques.
            </p>
            <ul className="liste-nue">
              {data.horsPortee.map((piece) => (
                <li key={piece.itemId ?? piece.referenceId}>
                  {piece.libelle === '' ? piece.designation : piece.libelle}
                  {piece.serialNumber !== null && (
                    <span className="faible mono"> · {piece.serialNumber}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
